import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableList, Typography, TableListActions, DropdownMenu } from 'seagull';
import { LinkIcon, EyeLine, EditLine, EmailSent, Download, DocumentCopy, TrashLiner24, ActivityLiner24 } from 'seagull/icons';
import { getAgreementPdfUrl } from '~/business-logic/relationshipLogic';
import { ClientPreview } from '~/components/ClientPreview/ClientPreview';
import { useOverlayContext, useIoContext, useStore } from '~/contexts';
import { Intent } from '~/infra/access-control';
import { relationshipSummaryModel, timeModel } from '~/models';
import { isExpired } from '~/models/relationship-summary';
import { selectors } from '~/store/state-selectors';
import { getStatusUIData, getRowAndMainAction } from './ProposalsDashboard.utils';
import { WithdrawModal } from './WithdrawModal/WithdrawModal';
export default function ProposalsDashboardRow(props) {
    const { proposal, isVendor, index, refresh, onViewActivityLog, onViewProposal, proposalActions } = props;
    const expired = isExpired(proposal);
    const history = useHistory();
    const overlayContext = useOverlayContext();
    const { openNewTab } = useIoContext();
    const heapId = proposal.isSample ? 'test-proposal' : 'proposals';
    const pdfHost = useStore(selectors.pdfHost);
    const opposingPartyName = relationshipSummaryModel.opposingPartyName(proposal, isVendor);
    const { statusText, statusSkin, lastUpdateText } = getStatusUIData(proposal, isVendor);
    const sellerFullName = relationshipSummaryModel.sellerFullName(proposal);
    const { getCancelProposalAction, getCopyProposalLinkAction, getDuplicateProposalAction, getEditProposalAction, getSendProposalReminderAction } = proposalActions;
    const { rowProps, mainAction } = getRowAndMainAction({
        proposal,
        isVendor,
        heapId,
        history,
        openClientPreview
    });
    const onDownloadPDF = (proposal)=>{
        openNewTab(getAgreementPdfUrl(proposal.id, pdfHost));
    };
    function renderSendProposalReminderAction() {
        const { intent, disabled, text, onClick } = getSendProposalReminderAction(proposal.id, proposal.isSample);
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: intent,
            disabled: disabled,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
            testId: "proposals-row-send-reminder",
            children: text
        }, "reminder".concat(proposal.id));
    }
    function renderCopyProposalLinkAction() {
        const { intent, text, onClick } = getCopyProposalLinkAction(proposal);
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: intent,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
            testId: proposal.isSample ? 'test-proposal-copy-link' : 'proposals-row-copy-link',
            children: text
        }, "copy-link-".concat(proposal.id));
    }
    function renderDuplicateProposalAction() {
        const { intent, disabled, text, onClick } = getDuplicateProposalAction(proposal.id, {
            isSampleProposal: proposal.isSample,
            draftId: proposal.draftId
        });
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: intent,
            iconStart: /*#__PURE__*/ _jsx(DocumentCopy, {}),
            disabled: disabled,
            testId: "proposals-row-duplicate",
            onClick: onClick,
            children: text
        }, "duplicate".concat(proposal.id));
    }
    function renderEditProposalAction() {
        const { text, intent, disabled, onClick } = getEditProposalAction(proposal, 'proposalsDashboard');
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: intent,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(EditLine, {}),
            disabled: disabled,
            testId: "proposals-row-withdraw-to-edit",
            children: text
        }, "withdraw-to-edit".concat(proposal.id));
    }
    function renderCancelProposalAction() {
        const { text, intent } = getCancelProposalAction(proposal.id, refresh);
        const withdrawProposal = ()=>{
            overlayContext.showModal({
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(WithdrawModal, {
                        openedBy: "proposals-dashboard",
                        relationshipId: proposal.id,
                        draftId: proposal.draftId,
                        onSuccess: refresh,
                        dismissModal: dismissModal
                    });
                }
            });
        };
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            skin: "error",
            intent: intent,
            iconStart: /*#__PURE__*/ _jsx(TrashLiner24, {}),
            testId: proposal.isSample ? 'test-proposal-cancel' : 'proposals-row-cancel',
            onClick: withdrawProposal,
            children: text
        }, "cancel".concat(proposal.id));
    }
    function openClientPreview() {
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "relationshipId",
                    relationshipId: proposal.id
                }, props))
        });
    }
    const actions = compact([
        isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.VendorViewProposal,
            iconStart: /*#__PURE__*/ _jsx(EyeLine, {}),
            testId: "proposals-row-view-proposal",
            "data-heapid": "".concat(heapId, "-review-btn"),
            onClick: ()=>onViewProposal(proposal),
            children: "View"
        }, "view-proposal".concat(proposal.id)),
        isVendor && renderEditProposalAction(),
        isVendor && !expired && renderCopyProposalLinkAction(),
        isVendor && !expired && renderSendProposalReminderAction(),
        isVendor && renderDuplicateProposalAction(),
        isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            iconStart: /*#__PURE__*/ _jsx(ActivityLiner24, {}),
            testId: "view-activity-log",
            onClick: ()=>onViewActivityLog(proposal),
            children: "View activity log"
        }, "activity".concat(proposal.id)),
        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.DownloadAgreementPdf,
            iconStart: /*#__PURE__*/ _jsx(Download, {}),
            disabled: proposal.isSample,
            testId: "proposals-row-download-pdf",
            onClick: ()=>onDownloadPDF(proposal),
            children: "Download PDF"
        }, "dowload-pdf-".concat(proposal.id)),
        isVendor && renderCancelProposalAction()
    ]);
    return /*#__PURE__*/ _jsx(TableList.Row, _object_spread_props(_object_spread({
        intent: mainAction.intent
    }, rowProps), {
        rowId: proposal.id,
        items: [
            {
                value: /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "proposals-row-".concat(index, "-name"),
                    children: [
                        opposingPartyName,
                        proposal.title && proposal.title !== opposingPartyName && /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            children: [
                                "\xa0(",
                                proposal.title,
                                ")"
                            ]
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: proposal.createdOn ? timeModel.formatDate(proposal.createdOn) : ''
                })
            },
            {
                value: sellerFullName != null && /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: sellerFullName
                })
            },
            {
                value: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "row-".concat(index, "-status"),
                            "data-skin": statusSkin,
                            skin: statusSkin,
                            weight: "normal",
                            children: statusText
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            size: "small",
                            testId: "row-".concat(index, "-status-update"),
                            "data-skin": statusSkin,
                            skin: statusSkin === 'error' ? 'error' : 'secondary',
                            children: lastUpdateText
                        })
                    ]
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            buttonText: mainAction.buttonText,
            buttonIntent: mainAction.intent,
            testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
            btnHeapId: mainAction.btnHeapId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            onClickButton: mainAction.onClickButton,
            children: actions.length > 0 && actions
        }),
        testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
        "data-heapid": heapId
    }), proposal.id);
}
